// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useRegistrarDelegatedMint } from '@hooks/mutations/registry/mint/useRegistrarDelegatedMint';
import { useCallback, useState } from 'react';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { useLimits } from '../../../contexts/LimitsContext';
import { useRegistrarHoldingOperationsDelegationsByParty } from '../../../hooks/queries/registry/registrar/useRegistrarHoldingOperationsDelegationsByParty';
import { useInstrumentConfigurationsById } from '../../../hooks/queries/useGetAllInstrumentConfigurations';
import { validateMintAmount } from '../../../utils/getValidateAmountMessage';

type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export const RegistrarMintDialog = ({ open, onClose }: DialogProps) => {
  const [tokenId, setTokenId] = useState<string>('');
  const [targetParty, setTargetParty] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [label, setLabel] = useState<string>('');
  const [reference, setReference] = useState<string>('');

  const { mintLimit } = useLimits();
  const amountValidation = validateMintAmount(amount, mintLimit);
  const instruments = useInstrumentConfigurationsById();
  const holdingRightsDelegations = useRegistrarHoldingOperationsDelegationsByParty();

  const registrarDelegatedMint = useRegistrarDelegatedMint();
  const resetState = () => {
    setTokenId('');
    setTargetParty('');
    setAmount('');
    setLabel('');
    setReference('');
  };

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose]);

  const doMint = async () => {
    const instrument = instruments[tokenId];

    if (!instrument) {
      // This should never happen, since the button is disabled if the instrument
      // cannot be found among the known configurations.
      throw Error(`Cannot find instrument configuration for token: ${tokenId}`);
    }

    const delegation = holdingRightsDelegations[targetParty];

    if (!delegation) {
      // This should never happen, since the button is disabled if the instrument
      // cannot be found among the known configurations.
      throw Error(`Cannot find delegation for token: ${targetParty}`);
    }

    await registrarDelegatedMint({
      party: targetParty,
      amount,
      source: instrument.payload.registrar,
      instrumentId: instrument.payload.defaultIdentifier.id,
      reference,
      delegationCid: delegation.contractId,
    });
    handleClose();
  };

  const disableSubmit =
    !amountValidation.isValid ||
    !amount ||
    !tokenId ||
    !instruments[tokenId] ||
    !targetParty ||
    !holdingRightsDelegations[targetParty];
  return (
    <FormDialog
      open={open}
      title="Mint"
      onCancel={handleClose}
      onSubmit={doMint}
      submitName="Mint"
      isSubmitDisabled={disableSubmit}
    >
      <TextInput
        enableTopLabel
        label="Instrument ID"
        value={tokenId}
        setValue={setTokenId}
        required
      />
      <TextInput
        enableTopLabel
        label="Wallet"
        value={targetParty}
        setValue={setTargetParty}
        required
      />
      <TextInput
        enableTopLabel
        type="number"
        validationMessage={amountValidation.message}
        label="Amount"
        value={amount}
        setValue={setAmount}
        inputProps={{ step: 'any' }}
        required
      />
      <TextInput enableTopLabel label="Label" value={label} setValue={setLabel} hidden />
      <TextInput enableTopLabel label="Reference" value={reference} setValue={setReference} />
    </FormDialog>
  );
};
